import type {
  AdAbandonDto,
  AdAbandonReason,
  AdCompletionDto,
  AdexiumSlotDto,
  AdSessionDto,
  AdSlotDto,
  AdSlotPurpose,
  BonusAdPurpose,
  InterstitialSlotDto,
  UslSlotDto,
} from '@pxc/shared';
import { useAdStore } from '../stores/ad.store.js';
import { ApiError } from './api.js';
import {
  AdClosedEarlyError,
  AdDismissedError,
  AdTooSoonError,
  AdUnavailableError,
  createAdRunner,
  playAdsgramInterstitial,
  preloadAdsgram,
} from './ads.js';
import { playAdexium, prepareAdexium } from './adexium.js';
import { humanPass } from './captcha.js';
import { playUsl, prepareUsl } from './usl.js';
import { api } from './endpoints.js';
import { loadGigapub, playGigapub } from './gigapub.js';
import { setClosingConfirmation } from './telegram.js';

/**
 * The steps every rewarded ad goes through, shared by the miners' shifts
 * (features/mining/useMining.ts), the bonus ads — the daily combo's reward and
 * the check-in (`watchBonusAd`) — and the task centre's slots, GigaPub's
 * (`watchGigapubAd`), Adsgram's interstitial (`watchInterstitialAd`),
 * Adexium's (`watchAdexiumAd`) and USL's (`watchUslAd`): the human check
 * before an ad, and what follows it.
 *
 * None of them grants anything: the server confirms the ad and pays.
 */

/**
 * How much of a human-check pass must be left to rely on it: the ad request
 * still has to reach the server inside the window.
 */
const PASS_MARGIN_MS = 10_000;

/** The human check the server asked for, from a CAPTCHA_REQUIRED refusal. */
export function requiredCheck(error: unknown): { siteKey: string; windowSeconds: number } | null {
  if (!(error instanceof ApiError) || error.code !== 'CAPTCHA_REQUIRED') return null;
  const details = (error.details ?? {}) as { siteKey?: unknown; windowSeconds?: unknown };
  if (typeof details.siteKey !== 'string' || !details.siteKey) return null;
  return {
    siteKey: details.siteKey,
    windowSeconds: typeof details.windowSeconds === 'number' ? details.windowSeconds : 120,
  };
}

/**
 * The human check before an ad ("verify you are human", Cloudflare
 * Turnstile): a shift's, and the GigaPub slot's when the panel asks for it.
 *
 * The rule is the server's: it opens no such ad without a pass from
 * the last couple of minutes (the panel's window), and only it records a pass,
 * after asking Cloudflare itself. So this only decides whether to show it:
 * not while a pass from moments ago still holds, and not when the server says
 * the check is off. Resolves false when the player closes the window, or when
 * the check could not be shown (the window then says why).
 */
export async function passHumanCheck(asked: { siteKey: string; windowSeconds: number } | null = null): Promise<boolean> {
  let check = asked;
  if (!check) {
    if (humanPass.validFor(PASS_MARGIN_MS)) return true;
    const status = await api.captcha.status();
    if (!status.required || !status.siteKey) return true;
    if (status.validUntil !== null && status.validUntil - status.serverTime > PASS_MARGIN_MS) {
      humanPass.set(status.validUntil, status.serverTime);
      return true;
    }
    check = { siteKey: status.siteKey, windowSeconds: status.windowSeconds };
  }
  return useAdStore.getState().askHuman(check.siteKey, check.windowSeconds);
}

/** What the app saw of a tap inside the ad: how it showed, and how long the player stayed away after. */
export interface AdClick {
  via: string | null;
  dwellMs: number | null;
}

/**
 * Asks the server to count the ad, waiting out the ad network's confirmation.
 *
 * With a server-to-server network (Adsgram's Reward URL) the confirmation can
 * land a moment after the ad closes; until then the server answers
 * AD_CONFIRMATION_PENDING and the session stays open, so the request is simply
 * repeated. Anything else is final.
 */
export async function completeWhenConfirmed(
  adSessionId: string,
  nonce: string,
  providerToken: string | null,
  click: AdClick,
): Promise<AdCompletionDto> {
  const delaysMs = [1000, 1500, 2000, 2500, 3000, 4000, 5000, 6000];
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await api.ads.complete(adSessionId, nonce, providerToken, click);
    } catch (error) {
      const pending = error instanceof ApiError && error.code === 'AD_CONFIRMATION_PENDING';
      const delay = delaysMs[attempt];
      if (!pending || delay === undefined) throw error;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}

/** Why the app gives up on an ad session, as the server records it. */
export function abandonReason(error: unknown): AdAbandonReason {
  if (error instanceof AdDismissedError) return 'DISMISSED';
  if (error instanceof AdTooSoonError) return 'TOO_SOON';
  if (error instanceof AdUnavailableError) return 'UNAVAILABLE';
  if (error instanceof ApiError && error.code === 'AD_CONFIRMATION_PENDING') return 'UNCONFIRMED';
  return 'ERROR';
}

/**
 * An ad that did not play to the end, or whose confirmation never came, must
 * not count later behind the user's back: its session is closed on the server
 * (`abandon`), and a late Reward URL call cannot count a closed session. If
 * the network's confirmation did land meanwhile, the ad was seen — then it is
 * completed and counted instead of being reported as lost. Otherwise the
 * original error is raised again, to be shown as "that ad did not count".
 */
export async function closeOrRecover(
  session: AdSessionDto,
  error: unknown,
  onRecover: () => void,
  click: AdClick,
): Promise<AdCompletionDto> {
  let closed: AdAbandonDto | null = null;
  try {
    closed = await api.ads.abandon(session.adSessionId, session.nonce, abandonReason(error));
  } catch {
    closed = null;
  }
  if (closed?.status !== 'COMPLETED') throw error;
  onRecover();
  return await api.ads.complete(session.adSessionId, session.nonce, null, click);
}

/** Where a bonus ad is: opening its session, playing, or waiting for the server to confirm and pay. */
export type BonusAdPhase = 'loading' | 'playing' | 'verifying';

/**
 * A rewarded ad that pays a bonus — the daily combo's reward or today's
 * check-in: open its session (the server refuses when there is nothing to
 * pay), play it, and let the server confirm it and pay. Resolves with what the
 * server paid (COMBO_PAID / CHECKIN_PAID); rejects with what went wrong, the
 * session closed so that nothing counts it later.
 *
 * The network's own screen covers the app while the ad plays, so there is no
 * overlay of ours: `onPhase` lets the button say what is happening.
 */
export async function watchBonusAd(
  purpose: BonusAdPurpose,
  onPhase: (phase: BonusAdPhase) => void = () => {},
): Promise<AdCompletionDto> {
  onPhase('loading');
  // Closing the Mini App mid-ad would lose it: Telegram asks first.
  setClosingConfirmation(true);
  try {
    const session = await api.ads.createBonusSession(purpose);
    const click: AdClick = { via: null, dwellMs: null };
    try {
      const { providerToken } = await createAdRunner(session.provider).run(session, {
        onStarted: () => onPhase('playing'),
        onProgress: () => {},
        onClicked: (via) => {
          click.via ??= via;
        },
        onClickDwell: (ms) => {
          click.dwellMs = ms;
        },
      });
      onPhase('verifying');
      return await completeWhenConfirmed(session.adSessionId, session.nonce, providerToken, click);
    } catch (error) {
      return await closeOrRecover(session, error, () => onPhase('verifying'), click);
    }
  } finally {
    setClosingConfirmation(false);
  }
}

/** The player closed the human check before an ad: nothing to say, nothing was lost. */
export class HumanCheckDeclined extends Error {
  constructor() {
    super('The human check was closed.');
    this.name = 'HumanCheckDeclined';
  }
}

/** Where a slot's ad is: the human check, its session and script, playing, or the server paying. */
export type SlotAdPhase = 'checking' | 'loading' | 'playing' | 'verifying';

/**
 * A session for one ad of the task centre's slots, which the server opens
 * only within the slot's rules — on, under the day's limit, past the pause. A
 * human-check pass the app believed in may have lapsed on the server: then
 * the check once more, and the session again.
 */
async function openSlotSession(purpose: AdSlotPurpose): Promise<AdSessionDto> {
  try {
    try {
      return await api.ads.createSlotSession(purpose);
    } catch (error) {
      const asked = requiredCheck(error);
      if (!asked) throw error;
      humanPass.clear();
      if (!(await passHumanCheck(asked))) throw new HumanCheckDeclined();
      return await api.ads.createSlotSession(purpose);
    }
  } finally {
    // The human check's window, if it came up, gives way to the network's player.
    useAdStore.getState().reset();
  }
}

/**
 * One ad of the task centre's GigaPub slot.
 *
 * The human check first when the slot asks for it (the same window as the
 * shifts': a pass from moments ago still holds); then a session the server
 * opens only within the slot's rules — on, under the day's limit, past the
 * pause — while GigaPub's script loads; then GigaPub's own player over the app;
 * then the server's word that it paid. Resolves with what the server paid
 * (GIGAPUB_PAID); rejects with what went wrong, the session closed so that
 * nothing counts it later.
 *
 * GigaPub confirms nothing to the server, so the server times the session:
 * an ad that ended before the session's minimum is held here the difference
 * ("verifying") rather than refused.
 */
export async function watchGigapubAd(
  slot: AdSlotDto,
  onPhase: (phase: SlotAdPhase) => void = () => {},
): Promise<AdCompletionDto> {
  onPhase('checking');
  if (slot.humanCheck && !(await passHumanCheck())) throw new HumanCheckDeclined();

  onPhase('loading');
  void loadGigapub(slot.projectId).catch(() => undefined);
  const session = await openSlotSession('GIGAPUB_REWARD');

  const openedAt = Date.now();
  setClosingConfirmation(true);
  try {
    try {
      await loadGigapub(session.gigapub?.projectId ?? slot.projectId);
      onPhase('playing');
      await playGigapub(slot.placement);
    } catch (error) {
      await api.ads.abandon(session.adSessionId, session.nonce, abandonReason(error)).catch(() => undefined);
      throw error;
    }
    onPhase('verifying');
    const wait = session.minWatchSeconds * 1000 + 600 - (Date.now() - openedAt);
    if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
    return await api.ads.complete(session.adSessionId, session.nonce, null);
  } finally {
    setClosingConfirmation(false);
  }
}

/**
 * One ad of the task centre's Adsgram interstitial slot, run as GigaPub's is:
 * the human check first when the slot asks for it; a session the server opens
 * only within the slot's rules, while Adsgram's SDK loads; the interstitial
 * over the app, from the slot's own `int-` block; then the server's word that
 * it paid (INTERSTITIAL_PAID).
 *
 * An interstitial can be closed before it ends, and Adsgram tells the server
 * nothing either way, so the slot's minimum is applied here first: one that
 * was not on screen that long is not sent for payment — its session is closed
 * and the player told how long to keep it open (AdClosedEarlyError). The
 * server holds a forged client to the same minimum by the session's age.
 */
export async function watchInterstitialAd(
  slot: InterstitialSlotDto,
  onPhase: (phase: SlotAdPhase) => void = () => {},
): Promise<AdCompletionDto> {
  onPhase('checking');
  if (slot.humanCheck && !(await passHumanCheck())) throw new HumanCheckDeclined();

  onPhase('loading');
  void preloadAdsgram().catch(() => undefined);
  const session = await openSlotSession('ADSGRAM_INT_REWARD');

  const openedAt = Date.now();
  const minimumMs = session.minWatchSeconds * 1000;
  setClosingConfirmation(true);
  try {
    try {
      const { shownMs } = await playAdsgramInterstitial(session.adsgram?.blockId ?? slot.blockId, {
        onStarted: () => onPhase('playing'),
      });
      if (shownMs < minimumMs) throw new AdClosedEarlyError(session.minWatchSeconds);
    } catch (error) {
      await api.ads.abandon(session.adSessionId, session.nonce, abandonReason(error)).catch(() => undefined);
      throw error;
    }
    onPhase('verifying');
    // The session opened before the ad began, so its minimum is all but always met by now.
    const wait = minimumMs + 600 - (Date.now() - openedAt);
    if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
    return await api.ads.complete(session.adSessionId, session.nonce, null);
  } finally {
    setClosingConfirmation(false);
  }
}

/**
 * One ad of the task centre's Adexium slot, run as Adsgram's interstitial is:
 * the human check first when the slot asks for it; a session the server opens
 * only within the slot's rules, while Adexium's widget gets ready; Adexium's
 * interstitial over the app, from the slot's widget; then the server's word
 * that it paid (ADEXIUM_PAID).
 *
 * Adexium tells the server nothing either way, so the slot's minimum is
 * applied here first: an ad closed sooner — unless Adexium said it played to
 * its end — is not sent for payment, its session is closed and the player
 * told how long to keep it open (AdClosedEarlyError). The server holds a
 * forged client to the same minimum by the session's age, and an ad that
 * ended sooner is held here the difference ("verifying").
 */
export async function watchAdexiumAd(
  slot: AdexiumSlotDto,
  onPhase: (phase: SlotAdPhase) => void = () => {},
): Promise<AdCompletionDto> {
  onPhase('checking');
  if (slot.humanCheck && !(await passHumanCheck())) throw new HumanCheckDeclined();

  onPhase('loading');
  void prepareAdexium(slot.widgetId).catch(() => undefined);
  const session = await openSlotSession('ADEXIUM_REWARD');

  const openedAt = Date.now();
  const minimumMs = session.minWatchSeconds * 1000;
  setClosingConfirmation(true);
  try {
    try {
      const { shownMs, completed } = await playAdexium(session.adexium?.widgetId ?? slot.widgetId, {
        onStarted: () => onPhase('playing'),
      });
      if (shownMs < minimumMs && !completed) throw new AdClosedEarlyError(session.minWatchSeconds);
    } catch (error) {
      await api.ads.abandon(session.adSessionId, session.nonce, abandonReason(error)).catch(() => undefined);
      throw error;
    }
    onPhase('verifying');
    const wait = minimumMs + 600 - (Date.now() - openedAt);
    if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
    return await api.ads.complete(session.adSessionId, session.nonce, null);
  } finally {
    setClosingConfirmation(false);
  }
}

/** What USL handed the page with the reward, as the provider token the server keeps (2,048 characters at most). */
function rewardToken(reward: unknown): string | null {
  if (reward === undefined || reward === null) return null;
  let text: string;
  try {
    text = typeof reward === 'string' ? reward : JSON.stringify(reward);
  } catch {
    text = String(reward);
  }
  return text ? text.slice(0, 2000) : null;
}

/**
 * One ad of the task centre's USL slot, run as GigaPub's is: the human check
 * first when the slot asks for it; a session the server opens only within the
 * slot's rules, while USL's SDK gets ready; USL's rewarded ad over the app,
 * from the slot's placement; then the server's word that it paid (USL_PAID).
 *
 * An ad counts only once USL said its reward was earned (`onRewardEarned`):
 * closed sooner, its session is closed and the player told to watch it to the
 * end (AdDismissedError). USL confirms nothing to the server, so the server
 * holds a forged client to the session's minimum age, and an ad that ended
 * sooner is held here the difference ("verifying").
 */
export async function watchUslAd(
  slot: UslSlotDto,
  onPhase: (phase: SlotAdPhase) => void = () => {},
): Promise<AdCompletionDto> {
  onPhase('checking');
  if (slot.humanCheck && !(await passHumanCheck())) throw new HumanCheckDeclined();

  onPhase('loading');
  void prepareUsl(slot.apiKey, slot.placementId).catch(() => undefined);
  const session = await openSlotSession('USL_REWARD');

  const openedAt = Date.now();
  setClosingConfirmation(true);
  try {
    let token: string | null;
    try {
      onPhase('playing');
      const { reward } = await playUsl(slot.apiKey, session.usl?.placementId ?? slot.placementId);
      token = rewardToken(reward);
    } catch (error) {
      await api.ads.abandon(session.adSessionId, session.nonce, abandonReason(error)).catch(() => undefined);
      throw error;
    }
    onPhase('verifying');
    const wait = session.minWatchSeconds * 1000 + 600 - (Date.now() - openedAt);
    if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
    return await api.ads.complete(session.adSessionId, session.nonce, token);
  } finally {
    setClosingConfirmation(false);
  }
}
